// no-spec-needed: infrastructure error page shell; no standalone user-visible feature
'use client';

import { clientLogger } from '@client/log-tunnel-provider';
import { useEffect } from 'react';

interface CenteredMessageCardProps {
  readonly title: string;
  readonly message: string;
  readonly variant?: 'page' | 'segment';
  readonly children?: React.ReactNode;
}

export function CenteredMessageCard({
  title,
  message,
  variant = 'page',
  children,
}: CenteredMessageCardProps) {
  const containerClasses =
    variant === 'page'
      ? 'flex min-h-screen items-center justify-center bg-background p-8'
      : 'flex min-h-[60vh] items-center justify-center p-8';

  return (
    <div className={containerClasses}>
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-muted-foreground text-sm">{message}</p>
        {children}
      </div>
    </div>
  );
}

interface ErrorPageShellProps {
  /** Error from Next.js's route-level error boundary. */
  readonly error: Error & { digest?: string };
  /** Reset callback from Next.js's route-level error boundary. */
  readonly reset: () => void;
  /** Stable identifier used in the structured log (e.g., `dashboard`, `root`). */
  readonly logScope: string;
  /** Page heading (h1). Defaults to "Something went wrong". */
  readonly heading?: string;
  /** Body paragraph text. */
  readonly body?: string;
  /**
   * `'page'` covers the whole viewport (default — root error.tsx).
   * `'segment'` reserves only ~60vh, leaving room for surrounding chrome.
   */
  readonly variant?: 'page' | 'segment';
}

const DEFAULT_HEADING = 'Something went wrong';
const DEFAULT_BODY =
  'An unexpected error occurred. Please try again, and if the problem persists, try refreshing the page.';

/**
 * Shared shell for Next.js route-level error boundaries (`app/error.tsx`,
 * `app/dashboard/error.tsx`). `app/global-error.tsx` cannot use this because
 * it replaces the root layout and must inline its markup.
 */
export function ErrorPageShell({
  error,
  reset,
  logScope,
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
  variant = 'page',
}: ErrorPageShellProps) {
  useEffect(() => {
    clientLogger.error('Unhandled error caught by error boundary', {
      logScope,
      error,
      digest: error.digest,
    });
  }, [error, logScope]);

  return (
    <CenteredMessageCard title={heading} message={body} variant={variant}>
      <button
        type="button"
        onClick={reset}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Try again
      </button>
    </CenteredMessageCard>
  );
}
